import React from 'react';
import { Box, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

// STEP INDEX คือลำดับจริงของ workflow (ดู services/marketing/controllers/opportunity.controller.js
// / getSelectableStatuses) — WON/LOST/CANCELLED รวมเป็นขั้นสุดท้ายเดียวกัน (index 3)
const STEP_INDEX = {
  WAITING: 0, IN_PROGRESS: 1, ON_HOLD: 2, WON: 3, LOST: 3, CANCELLED: 3,
};

// glow สีตาม theme.js (primary #4f46e5 / success #10b981 / error #ef4444) — เขียนตรง ๆ
// แทน alpha() เพราะใช้แค่จุดเดียวนี้ ไม่คุ้มดึง useTheme มาเพิ่ม dependency ให้ component
const GLOW = {
  primary: 'rgba(79, 70, 229, 0.28)',
  success: 'rgba(16, 185, 129, 0.28)',
  error: 'rgba(239, 68, 68, 0.28)',
};

// timeline แนวนอนของสถานะโครงการ — เป็นภาพจริงของ workflow ไม่ใช่แค่ตกแต่ง เห็นทันทีว่า
// ดีลนี้อยู่ขั้นไหนของกระบวนการโดยไม่ต้องอ่านตัวหนังสือ — ขนาดกะทัดรัด (วงกลม 22px) ให้พอดีแถบหัว
// เดียวของ OpportunityDetailHeader.js ถ้าเปลี่ยนขนาดวงกลม ต้องแก้ top ของเส้นเชื่อม (= ครึ่งวงกลม - 1) ด้วย
const DOT = 22;
export function OpportunityPipeline({ status }) {
  const steps = [
    { label: 'เริ่มต้น' },
    { label: 'ดำเนินการ' },
    { label: 'ชะลอ/รอ' },
    {
      label: status === 'WON' ? 'ได้งาน'
        : status === 'LOST' ? 'ไม่ได้งาน'
        : status === 'CANCELLED' ? 'ยกเลิก' : 'ปิดงาน',
    },
  ];
  const currentIndex = STEP_INDEX[status] ?? 0;
  const isLost = status === 'LOST' || status === 'CANCELLED';
  const isWon = status === 'WON';

  return (
    <Box sx={{ display: 'flex', width: '100%' }}>
      {steps.map((step, idx) => {
        const isFinalStep = idx === 3;
        const done = idx < currentIndex;
        const current = idx === currentIndex;
        const filled = done || current;

        let paletteKey = 'primary';
        if (isFinalStep && current) paletteKey = isLost ? 'error' : isWon ? 'success' : 'primary';

        return (
          // eslint-disable-next-line react/no-array-index-key
          <Box key={idx} sx={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {idx > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: DOT / 2 - 1,
                  right: '50%',
                  width: '100%',
                  height: 2,
                  bgcolor: idx <= currentIndex ? `${paletteKey}.main` : 'grey.200',
                  zIndex: 0,
                  transition: 'background-color 0.3s ease',
                }}
              />
            )}
            <Box
              sx={{
                width: DOT,
                height: DOT,
                borderRadius: '50%',
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: filled ? `${paletteKey}.main` : 'background.paper',
                border: '2px solid',
                borderColor: filled ? `${paletteKey}.main` : 'grey.300',
                color: filled ? '#fff' : 'grey.400',
                transition: 'all 0.3s ease',
                '@keyframes pipelinePulse': {
                  '0%': { boxShadow: `0 0 0 0 ${GLOW[paletteKey]}` },
                  '70%': { boxShadow: '0 0 0 6px rgba(0,0,0,0)' },
                  '100%': { boxShadow: '0 0 0 0 rgba(0,0,0,0)' },
                },
                animation: current ? 'pipelinePulse 1.8s ease-out infinite' : 'none',
              }}
            >
              {isFinalStep && current && isWon && <CheckIcon sx={{ fontSize: 14 }} />}
              {isFinalStep && current && isLost && <CloseIcon sx={{ fontSize: 14 }} />}
              {(!isFinalStep || !current) && done && <CheckIcon sx={{ fontSize: 13 }} />}
              {!done && !(isFinalStep && current) && (
                <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.62rem', lineHeight: 1 }}>{idx + 1}</Typography>
              )}
            </Box>
            <Typography
              variant="caption"
              noWrap
              sx={{
                mt: 0.5,
                fontSize: '0.7rem',
                lineHeight: 1.2,
                fontWeight: current ? 700 : 500,
                color: current ? `${paletteKey}.main` : 'text.secondary',
                textAlign: 'center',
                px: 0.5,
              }}
            >
              {step.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

export default OpportunityPipeline;
